import cn from 'classnames'
import { useCallback, useMemo, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { toFormat } from 'core/classes/helpers/DateHelper'
import {
  getEspActivityQuarter,
  getEspActivityTypeLabel,
  getEspFiscalQuarter,
  getEspMeetingReasonLabel,
  type IEspActivity,
  type IEspFiscalQuarter,
  parseEspDate,
  shiftEspFiscalQuarter,
} from 'partner360/features/executiveSponsorProgram'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../configs/translations'
import { CockpitIconButton } from '../../components/cockpitIconButton'

import styles from './CockpitEspQuarters.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.esp

/** The strip shows this many quarters, the current one last. */
const WINDOW = 4

/** Long enough for a smooth scroll to land before the feed marks the quarter it shows again. */
const SCROLL_LOCK_MS = 600

interface IQuarterGroup {
  quarter: IEspFiscalQuarter
  activities: IEspActivity[]
}

interface ICockpitEspQuartersProps {
  activities: IEspActivity[]
  onOpenActivity: (activityId: string) => void
}

/**
 * The engagements by fiscal quarter: a strip of the last four quarters, the current one last, with
 * the count of each, over a feed of every engagement, newest first, under the heading of its
 * quarter. A quarter picked on the strip scrolls the feed to it; scrolling the feed marks the
 * quarter at its top. The arrows move the strip a quarter back or forth, never past the current one.
 */
export const CockpitEspQuarters = ({ activities, onOpenActivity }: ICockpitEspQuartersProps) => {
  const t = useSafetyTranslation()
  const current = useMemo(() => getEspFiscalQuarter(new Date()), [])

  const groups = useMemo<IQuarterGroup[]>(() => {
    const sorted = [...activities].sort((a, b) => b.date.localeCompare(a.date))
    const byKey = new Map<string, IQuarterGroup>()

    sorted.forEach((activity) => {
      const quarter = getEspActivityQuarter(activity)
      const group = byKey.get(quarter.key) ?? { quarter, activities: [] }

      group.activities.push(activity)
      byKey.set(quarter.key, group)
    })

    return [...byKey.values()]
  }, [activities])

  const counts = useMemo(
    () => new Map(groups.map(({ quarter, activities: list }) => [quarter.key, list.length])),
    [groups]
  )

  const [windowEnd, setWindowEnd] = useState(current)
  const [selectedKey, setSelectedKey] = useState(() => {
    const latest = groups[0]?.quarter

    return latest && latest.key >= shiftEspFiscalQuarter(current, 1 - WINDOW).key
      ? latest.key
      : current.key
  })

  const quarters = useMemo(
    () =>
      Array.from({ length: WINDOW }, (_, index) =>
        shiftEspFiscalQuarter(windowEnd, index + 1 - WINDOW)
      ),
    [windowEnd]
  )
  const oldestKey = groups[groups.length - 1]?.quarter.key
  const canGoEarlier = Boolean(oldestKey && oldestKey < quarters[0].key)
  const canGoLater = windowEnd.key < current.key

  const feedRef = useRef<HTMLDivElement>(null)
  const isScrollLocked = useRef(false)

  const scrollFeedTo = useCallback((key: string) => {
    const feed = feedRef.current

    if (!feed) {
      return
    }

    const headings = [...feed.querySelectorAll<HTMLElement>('[data-quarter]')]
    /* A quarter with no engagement scrolls to the next older one that has some. */
    const target = headings.find(({ dataset }) => (dataset.quarter ?? '') <= key)

    isScrollLocked.current = true
    feed.scrollTo({ top: target ? target.offsetTop : feed.scrollHeight, behavior: 'smooth' })
    window.setTimeout(() => {
      isScrollLocked.current = false
    }, SCROLL_LOCK_MS)
  }, [])

  const pickQuarter = (quarter: IEspFiscalQuarter) => {
    setSelectedKey(quarter.key)
    scrollFeedTo(quarter.key)
  }

  /* The feed scrolled by hand: mark the quarter at its top, and bring it onto the strip. */
  const handleFeedScroll = () => {
    const feed = feedRef.current

    if (!feed || isScrollLocked.current) {
      return
    }

    const headings = [...feed.querySelectorAll<HTMLElement>('[data-quarter]')]
    const top = headings.filter(({ offsetTop }) => offsetTop <= feed.scrollTop + 4).pop()
    const key = (top ?? headings[0])?.dataset.quarter

    if (!key || key === selectedKey) {
      return
    }

    setSelectedKey(key)

    const group = groups.find(({ quarter }) => quarter.key === key)

    if (group && key < quarters[0].key) {
      setWindowEnd(shiftEspFiscalQuarter(group.quarter, WINDOW - 1))
    } else if (group && key > windowEnd.key) {
      setWindowEnd(group.quarter)
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.caption}>
        <Translation text={KEYS.engagements} span />
        <span aria-hidden="true"> · </span>
        <span>{activities.length}</span>
      </div>

      <div className={styles.strip}>
        <CockpitIconButton
          icon="kbase kbase-chevron-left"
          label={t(KEYS.earlier)}
          disabled={!canGoEarlier}
          onClick={() => setWindowEnd((end) => shiftEspFiscalQuarter(end, -1))}
        />
        <div className={styles.quarters}>
          {quarters.map((quarter) => {
            const count = counts.get(quarter.key) ?? 0

            return (
              <div key={quarter.key} className={styles.quarter}>
                <button
                  type="button"
                  className={cn(styles.cell, styles[`level${Math.min(3, count)}`], {
                    [styles.now]: quarter.key === current.key,
                    [styles.selected]: quarter.key === selectedKey,
                  })}
                  aria-pressed={quarter.key === selectedKey}
                  aria-label={`${quarter.label}: ${count}`}
                  onClick={() => pickQuarter(quarter)}
                >
                  {count || ''}
                </button>
                <span className={styles.quarterLabel}>{quarter.label}</span>
              </div>
            )
          })}
        </div>
        <CockpitIconButton
          icon="kbase kbase-chevron-right"
          label={t(KEYS.later)}
          disabled={!canGoLater}
          onClick={() => setWindowEnd((end) => shiftEspFiscalQuarter(end, 1))}
        />
      </div>

      {groups.length ? (
        <div ref={feedRef} className={styles.feed} onScroll={handleFeedScroll}>
          {groups.map(({ quarter, activities: list }) => (
            <section key={quarter.key}>
              <h4 className={styles.heading} data-quarter={quarter.key}>
                {quarter.label}
              </h4>
              {list.map((activity) => (
                <button
                  key={activity.id}
                  type="button"
                  className={styles.row}
                  onClick={() => onOpenActivity(activity.id)}
                >
                  <span className={styles.date}>
                    {toFormat(parseEspDate(activity.date), 'DD MMM')}
                  </span>
                  <span className={styles.what}>
                    <span className={styles.kind}>
                      {[
                        getEspActivityTypeLabel(activity.activityType),
                        getEspMeetingReasonLabel(activity.meetingReason),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {activity.outcome && <span className={styles.outcome}>{activity.outcome}</span>}
                  </span>
                </button>
              ))}
            </section>
          ))}
        </div>
      ) : (
        <p className={styles.empty}>
          <Translation text={KEYS.engagementsNone} span />
        </p>
      )}
    </div>
  )
}

CockpitEspQuarters.displayName = 'CockpitEspQuarters'
